/**
 * Subjects API Service - Enhanced wrapper
 *
 * Wraps the generated subjects API with enhanced cache invalidation to ensure
 * that when relationships (ownership, vehicle occupancy) are modified,
 * the related subject queries are properly invalidated.
 *
 * Pattern follows policeDepartmentsService.ts approach.
 */

import {
  type AllSubjectsResponse,
  type AllSubjectsWithRelationships,
  type CreateAnimal,
  type CreateOwnership,
  type CreateProperty,
  type CreateVehicle,
  type CreateVehicleOccupancy,
  enhancedApi as generatedApi,
  type ReadAnimal,
  type ReadProperty,
  type ReadPropertyWithOwners,
  type ReadVehicle,
  type ReadVehicleWithOccupants,
  type UpdateAnimal,
  type UpdateOwnership,
  type UpdateProperty,
  type UpdateVehicle,
  type UpdateVehicleOccupancy,
  type VehicleType,
} from './generated/subjectsService';

/**
 * Enhanced version of the generated API with granular cache invalidation.
 *
 * When relationships are created/updated/deleted, we invalidate:
 * 1. The specific subject detail queries
 * 2. The aggregate "with-relationships" queries
 * 3. The general subject list
 */
export const enhancedApi = generatedApi.enhanceEndpoints({
  endpoints: {
    // Vehicles - provide specific tags for granular invalidation
    vehiclesRetrieve: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'subjects' as const, id: arg.id },
              { type: 'subjects' as const, id: `vehicle-${arg.id}` },
              'subjects' as const,
            ]
          : ['subjects' as const],
    },
    vehiclesWithOccupants: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'subjects' as const, id: arg.id },
              { type: 'subjects' as const, id: `vehicle-with-occ-${arg.id}` },
              'subjects' as const,
            ]
          : ['subjects' as const],
    },

    // Properties - provide specific tags
    propertiesRetrieve: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'subjects' as const, id: arg.id },
              { type: 'subjects' as const, id: `property-${arg.id}` },
              'subjects' as const,
            ]
          : ['subjects' as const],
    },
    propertiesWithOwners: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'subjects' as const, id: arg.id },
              { type: 'subjects' as const, id: `property-with-own-${arg.id}` },
              'subjects' as const,
            ]
          : ['subjects' as const],
    },

    // Animals - provide specific tags
    animalsRetrieve: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { id: string; matterId: string },
      ) =>
        result
          ? [
              { type: 'subjects' as const, id: arg.id },
              { type: 'subjects' as const, id: `animal-${arg.id}` },
              'subjects' as const,
            ]
          : ['subjects' as const],
    },

    // Aggregate views - provide matter-specific tags
    allSubjectsList: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { matterId: string },
      ) => [
        { type: 'subjects' as const, id: `matter-${arg.matterId}` },
        'subjects' as const,
      ],
    },
    allSubjectsWithRelationships: {
      providesTags: (
        result: unknown,
        error: unknown,
        arg: { matterId: string },
      ) => [
        { type: 'subjects' as const, id: `matter-with-rel-${arg.matterId}` },
        'subjects' as const,
      ],
    },

    // Vehicle occupancy mutations should invalidate both the vehicle and the person
    vehicleOccupanciesCreate: {
      invalidatesTags: (
        result: unknown,
        error: unknown,
        arg: {
          matterId: string;
          createVehicleOccupancy: CreateVehicleOccupancy;
        },
      ) => [
        'subjects' as const,
        {
          type: 'subjects' as const,
          id: arg.createVehicleOccupancy.vehicle_id,
        },
        { type: 'parties' as const, id: arg.createVehicleOccupancy.person_id },
      ],
    },
    vehicleOccupanciesUpdate: {
      invalidatesTags: ['subjects' as const, 'parties' as const],
    },
    vehicleOccupanciesDelete: {
      invalidatesTags: ['subjects' as const, 'parties' as const],
    },

    // Ownership mutations should invalidate both the subject and the party
    ownershipsCreate: {
      invalidatesTags: (
        result: unknown,
        error: unknown,
        arg: { matterId: string; createOwnership: CreateOwnership },
      ) => [
        'subjects' as const,
        { type: 'subjects' as const, id: arg.createOwnership.subject_id },
        { type: 'parties' as const, id: arg.createOwnership.party_id },
      ],
    },
    ownershipsUpdate: {
      invalidatesTags: ['subjects' as const, 'parties' as const],
    },
    ownershipsDelete: {
      invalidatesTags: ['subjects' as const, 'parties' as const],
    },
  },
});

// Re-export all the hooks from the enhanced API
export const {
  useAnimalsCreateMutation,
  useAnimalsDeleteMutation,
  useAnimalsListQuery,
  useAnimalsRetrieveQuery,
  useAnimalsUpdateMutation,
  useAllSubjectsListQuery,
  useAllSubjectsWithRelationshipsQuery,
  useOwnershipsCreateMutation,
  useOwnershipsDeleteMutation,
  useOwnershipsListQuery,
  useOwnershipsRetrieveQuery,
  useOwnershipsUpdateMutation,
  usePropertiesCreateMutation,
  usePropertiesDeleteMutation,
  usePropertiesListQuery,
  usePropertiesRetrieveQuery,
  usePropertiesUpdateMutation,
  usePropertiesWithOwnersQuery,
  useVehicleOccupanciesCreateMutation,
  useVehicleOccupanciesDeleteMutation,
  useVehicleOccupanciesListQuery,
  useVehicleOccupanciesRetrieveQuery,
  useVehicleOccupanciesUpdateMutation,
  useVehiclesCreateMutation,
  useVehiclesDeleteMutation,
  useVehiclesListQuery,
  useVehiclesRetrieveQuery,
  useVehiclesUpdateMutation,
  useVehiclesWithOccupantsQuery,
} = enhancedApi;

// Re-export all types
export type {
  AllSubjectsResponse,
  AllSubjectsWithRelationships,
  CreateAnimal,
  CreateOwnership,
  CreateProperty,
  CreateVehicle,
  CreateVehicleOccupancy,
  ReadAnimal,
  ReadProperty,
  ReadPropertyWithOwners,
  ReadVehicle,
  ReadVehicleWithOccupants,
  UpdateAnimal,
  UpdateOwnership,
  UpdateProperty,
  UpdateVehicle,
  UpdateVehicleOccupancy,
  VehicleType,
};
